import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiMessageSquare, FiUsers, FiClock, FiCheckCircle, FiAlertCircle,
  FiSearch, FiFilter, FiMoreVertical, FiSend, FiPaperclip, FiPhone,
  FiVideo, FiArchive, FiStar, FiX, FiUser, FiActivity, FiTrendingUp
} = FiIcons;

const ChatManagement = () => {
  const [selectedChat, setSelectedChat] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [messageInput, setMessageInput] = useState('');

  const conversations = [
    {
      id: 1,
      user: 'Dr. Sarah Mitchell',
      avatar: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=40&h=40&fit=crop&crop=face',
      lastMessage: 'Thank you for the quick response!',
      timestamp: '2 min ago',
      unread: 2,
      status: 'active',
      type: 'support'
    },
    {
      id: 2,
      user: 'Dr. Michael Chen',
      avatar: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=40&h=40&fit=crop&crop=face',
      lastMessage: 'Can you help me with the referral process?',
      timestamp: '15 min ago',
      unread: 0,
      status: 'active',
      type: 'inquiry'
    },
    {
      id: 3,
      user: 'Dr. Lisa Rodriguez',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=40&h=40&fit=crop&crop=face',
      lastMessage: 'Perfect, that solved my issue!',
      timestamp: '1 hour ago',
      unread: 0,
      status: 'resolved',
      type: 'support'
    },
    {
      id: 4,
      user: 'Dr. James Wilson',
      avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5cd?w=40&h=40&fit=crop&crop=face',
      lastMessage: 'I need urgent assistance with...',
      timestamp: '2 hours ago',
      unread: 5,
      status: 'urgent',
      type: 'urgent'
    }
  ];

  const messages = [
    {
      id: 1,
      sender: 'user',
      text: 'Hi, I need help with setting up my dealroom',
      timestamp: '10:30 AM',
      avatar: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=40&h=40&fit=crop&crop=face'
    },
    {
      id: 2,
      sender: 'admin',
      text: 'Hello! I\'d be happy to help you with that. Can you tell me which specific aspect you\'re having trouble with?',
      timestamp: '10:31 AM',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face'
    },
    {
      id: 3,
      sender: 'user',
      text: 'I\'m trying to add participants but the button seems disabled',
      timestamp: '10:32 AM',
      avatar: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=40&h=40&fit=crop&crop=face'
    },
    {
      id: 4,
      sender: 'admin',
      text: 'I see. Make sure you\'ve completed the dealroom setup first. The participant button will be enabled once you\'ve added the basic information.',
      timestamp: '10:33 AM',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face'
    }
  ];

  const stats = {
    totalChats: 234,
    activeChats: 45,
    avgResponseTime: '2.5 min',
    satisfactionRate: 94.5
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'resolved': return 'bg-blue-100 text-blue-800';
      case 'urgent': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = conv.user.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || conv.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50 to-blue-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Chat Management</h1>
              <p className="text-gray-600 mt-1">Oversee and manage all user conversations</p>
            </div>
          </div>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-cyan-50 rounded-lg">
                <SafeIcon icon={FiMessageSquare} className="w-6 h-6 text-cyan-600" />
              </div>
              <span className="text-sm text-green-600 font-medium">+12%</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.totalChats}</p>
            <p className="text-sm text-gray-600 mt-1">Total Conversations</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-50 rounded-lg">
                <SafeIcon icon={FiActivity} className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-sm text-green-600 font-medium">+5</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.activeChats}</p>
            <p className="text-sm text-gray-600 mt-1">Active Chats</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <SafeIcon icon={FiClock} className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-sm text-green-600 font-medium">-8%</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.avgResponseTime}</p>
            <p className="text-sm text-gray-600 mt-1">Avg Response Time</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-50 rounded-lg">
                <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-purple-600" />
              </div>
              <span className="text-sm text-green-600 font-medium">+2.3%</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.satisfactionRate}%</p>
            <p className="text-sm text-gray-600 mt-1">Satisfaction Rate</p>
          </motion.div>
        </div>

        {/* Chat Interface */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 h-[600px]">
            {/* Conversations List */}
            <div className="lg:col-span-1 border-r border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <div className="relative mb-4">
                  <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search conversations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  {['all', 'active', 'urgent', 'resolved'].map((status) => (
                    <button
                      key={status}
                      onClick={() => setFilterStatus(status)}
                      className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                        filterStatus === status
                          ? 'bg-cyan-50 text-cyan-600 border border-cyan-200'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="overflow-y-auto h-[calc(600px-140px)]">
                {filteredConversations.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => setSelectedChat(conv)}
                    className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedChat?.id === conv.id ? 'bg-cyan-50' : ''
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="relative">
                        <img src={conv.avatar} alt={conv.user} className="w-12 h-12 rounded-full object-cover" />
                        {conv.unread > 0 && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                            <span className="text-xs text-white font-bold">{conv.unread}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-semibold text-gray-900 truncate">{conv.user}</p>
                          <span className="text-xs text-gray-500">{conv.timestamp}</span>
                        </div>
                        <p className="text-sm text-gray-600 truncate">{conv.lastMessage}</p>
                        <div className="flex items-center space-x-2 mt-2">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(conv.status)}`}>
                            {conv.status}
                          </span>
                          <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full">
                            {conv.type}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Chat Window */}
            <div className="lg:col-span-2 flex flex-col">
              {selectedChat ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <img src={selectedChat.avatar} alt={selectedChat.user} className="w-10 h-10 rounded-full object-cover" />
                      <div>
                        <p className="font-semibold text-gray-900">{selectedChat.user}</p>
                        <p className="text-sm text-gray-500">Active now</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                        <SafeIcon icon={FiPhone} className="w-5 h-5" />
                      </button>
                      <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                        <SafeIcon icon={FiVideo} className="w-5 h-5" />
                      </button>
                      <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                        <SafeIcon icon={FiStar} className="w-5 h-5" />
                      </button>
                      <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                        <SafeIcon icon={FiMoreVertical} className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex items-start space-x-3 ${
                          message.sender === 'admin' ? 'flex-row-reverse space-x-reverse' : ''
                        }`}
                      >
                        <img src={message.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                        <div className={`flex-1 max-w-md ${message.sender === 'admin' ? 'flex flex-col items-end' : ''}`}>
                          <div
                            className={`p-3 rounded-lg ${
                              message.sender === 'admin'
                                ? 'bg-cyan-600 text-white'
                                : 'bg-white text-gray-900 border border-gray-200'
                            }`}
                          >
                            <p className="text-sm">{message.text}</p>
                          </div>
                          <span className="text-xs text-gray-500 mt-1">{message.timestamp}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Message Input */}
                  <div className="p-4 border-t border-gray-200 bg-white">
                    <div className="flex items-center space-x-2">
                      <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                        <SafeIcon icon={FiPaperclip} className="w-5 h-5" />
                      </button>
                      <input
                        type="text"
                        placeholder="Type your message..."
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      />
                      <button className="p-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors">
                        <SafeIcon icon={FiSend} className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center bg-gray-50">
                  <div className="text-center">
                    <SafeIcon icon={FiMessageSquare} className="w-24 h-24 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">Select a conversation to start chatting</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ChatManagement;